import {
  Button,
  DotsVerticalIcon,
  Dropdown,
  DropdownMenu,
  type DropdownMenuItem,
  Modal,
  type Row,
  Table,
  type TableColumns,
} from '@bp/ui-components';
import { SchoolyearForm } from 'components/SchoolyearForm/SchoolyearForm';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';
import { graphql } from '../../../client/gql';
import { BpSubpage } from '../../../components/BpSubpage/BpSubpage';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useConfirm } from '../../../hooks/useConfirm';
import { useIsDesktop } from '../../../hooks/useIsDesktop';
import { usePermissionChecker } from '../../../hooks/usePermissionChecker';
import { niceDate } from '../../../utils/dateCalculations';
import { showErrorToast } from '../../../utils/showErrorToast';
import { showSuccessToast } from '../../../utils/showSuccessToast';

const DeleteSchoolyearMutationDocument = graphql(`
  mutation DeleteBpSchoolyear($uuid: ID!) {
    deleteBpSchoolYear(uuid: $uuid) {
      entitiesDeleted
    }
  }
`);

export type SchoolyearData = {
  name: string;
  uuid: string;
  start: string;
  end: string;
  managedBy: string;
  classesCount: number;
  groupsCount: number;
};

export const InstitutionSchoolyearsSubpageQueryDocument = graphql(`
  query InstitutionSchoolyearsSubpageData($organizationUuid: ID!) {
    schoolYears(where: { organization: { uuid: $organizationUuid } }) {
      uuid
      __typename
      foreignRefId
      name
      shortName
      start
      end
      createdAt
      managedBy
      autoSync
      classesAggregate {
        count
      }
      groupsAggregate {
        count
      }
    }
  }
`);

export const InstitutionSchoolyearsSubpage = () => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const profile = pimAuthClaims.getProfile();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();
  const isDesktop = useIsDesktop();

  const [, deleteSchoolyear] = useMutation(DeleteSchoolyearMutationDocument);

  const [currentSchoolyearUuid, setCurrentSchoolyearUuid] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const [{ data }] = useQuery({
    query: InstitutionSchoolyearsSubpageQueryDocument,
    variables: {
      organizationUuid,
      profile: profile.uuid,
    },
  });

  const perms = usePermissionChecker();

  const tableData =
    data?.schoolYears.map((s) => {
      return {
        name: s.name,
        uuid: s.uuid,
        start: s.start,
        end: s.end,
        managedBy: s.managedBy,
        classesCount: s.classesAggregate?.count ?? 0,
        groupsCount: s.groupsAggregate?.count ?? 0,
      };
    }) ?? [];

  const tableColumns: TableColumns<SchoolyearData>[] = [
    {
      header: t('schoolyears.titleSingular'),
      id: 'name',
      accessorKey: 'name',
      canExpand: true,
      size: 300,
    },
    {
      id: 'begin_end',
      header: t('common.timeframe'),
      cell: ({ row }) => `${niceDate(row.original.start, 'short')} - ${niceDate(row.original.end, 'short')}`,
      size: 400,
    },
    {
      header: t('common.managedBy'),
      id: 'managedBy',
      accessorKey: 'managedBy',
    },
  ];

  const handleClose = () => {
    setModalOpen(false);
    setCurrentSchoolyearUuid(null);
  };

  const handleEdit = (row: Row<SchoolyearData>) => {
    setCurrentSchoolyearUuid(row.original.uuid);
    setModalOpen(true);
  };

  const { ConfirmationDialog, confirm: confirmDelete } = useConfirm({
    defaultTitle: t('delete.headline'),
    defaultMessage: t('delete.message', {
      type: t('schoolyears.titleSingular'),
      context: 'neutrum',
    }),
    defaultConfirmText: t('delete.delete'),
  });

  const handleDelete = async (row: Row<SchoolyearData>) => {
    if (perms?.canDeleteSchoolyear({ uuid: organizationUuid })) {
      const { error } = await deleteSchoolyear({ uuid: row.original.uuid });
      error ? showErrorToast(error) : showSuccessToast(t('schoolyears.deleted'));
    }
  };

  const dropDownMenu = (row: Row<SchoolyearData>): DropdownMenuItem[] => {
    return [
      { label: t('common.edit'), onClick: () => handleEdit(row) },
      {
        label: t('delete.delete'),
        color: 'error',
        disabled: row.original.classesCount > 0 || row.original.groupsCount > 0,
        onClick: async () => {
          const res = await confirmDelete();
          if (res) {
            await handleDelete(row);
          }
        },
      },
    ];
  };

  return (
    <BpSubpage isForbidden={!perms?.canListSchoolyears({ uuid: organizationUuid })}>
      <Table
        isDesktop={isDesktop}
        data-cy={'schoolyearsList'}
        showActionBar
        showBorderRadius
        showShadow
        isOnWhite={false}
        actionBarSettings={{
          showGlobalFilter: false,
          showAddButton: perms?.canCreateSchoolyear({
            uuid: organizationUuid,
          }),
          addButtonText: t('common.addType', {
            type: t('schoolyears.titleSingular'),
          }),
        }}
        sorting={[{ id: 'name', desc: false }]}
        onAddClick={() => {
          setModalOpen(true);
        }}
        columns={tableColumns}
        data={tableData}
        lastCol={(row) => {
          return (
            <Dropdown trigger={<Button hierarchy={'tertiary'} icon={<DotsVerticalIcon />} />}>
              <DropdownMenu data={dropDownMenu(row)} />
            </Dropdown>
          );
        }}
      />
      <Modal title={t('common.schoolyear')} isOpen={modalOpen} onRequestClose={handleClose} width='s'>
        <SchoolyearForm onClose={handleClose} schoolyears={tableData} selectedSchoolyearUuid={currentSchoolyearUuid} />
      </Modal>
      <ConfirmationDialog />
    </BpSubpage>
  );
};
